import { isAxiosError } from "axios";
import { ZodError } from "zod";

const DEFAULT_MESSAGE =
  "The request could not be processed. Please check your file and try again.";

/**
 * Turn a FastAPI / ASP.NET-style error body into a short user-facing message.
 * Supports `{ detail: string }`, `{ detail: [{ msg }] }`, or a plain string body.
 */
export function formatApiErrorDetail(
  data: unknown,
  fallback = DEFAULT_MESSAGE,
): string {
  if (typeof data === "string" && data.trim() !== "") {
    return data;
  }

  if (data && typeof data === "object") {
    if ("detail" in data) {
      const { detail } = data as { detail: unknown };
      if (typeof detail === "string" && detail.trim() !== "") {
        return detail;
      }
      if (Array.isArray(detail)) {
        const messages = detail
          .map((item) => {
            if (typeof item === "string") return item;
            if (item && typeof item === "object" && "msg" in item) {
              return String((item as { msg: unknown }).msg);
            }
            return null;
          })
          .filter((message): message is string => Boolean(message));
        if (messages.length > 0) {
          return messages.join(" ");
        }
      }
    }

    if (
      "message" in data &&
      typeof (data as { message: unknown }).message === "string"
    ) {
      const message = (data as { message: string }).message.trim();
      if (message !== "") return message;
    }
  }

  return fallback;
}

/** Map thrown errors (axios, zod, Error) to a message safe to show in the UI. */
export function formatUserFacingError(
  err: unknown,
  fallback = DEFAULT_MESSAGE,
): string {
  if (err instanceof ZodError) {
    return fallback;
  }
  if (isAxiosError(err) && err.response?.data !== undefined) {
    return formatApiErrorDetail(err.response.data, fallback);
  }
  if (err instanceof Error && err.message.trim() !== "") {
    return err.message;
  }
  return fallback;
}
